import { Message, MessageSender, MessageType } from "@/app/store/types";
import { tryParse } from "@/app/utils/object";
import React, { useCallback, useMemo } from "react";
import styles from "./Quoted.module.scss";
import SvgClose from "@/app/icons/close.svg";
import { Popover } from "@douyinfe/semi-ui";
import { Image } from "@douyinfe/semi-ui";
import Locale from "@/app/locales";
import { isForbiddenMsg } from "@/app/store/formatMessage";

export enum QuotedFrom {
  ChatInput = "chatInput",
  Msg = "msg",
}
export default function Quoted({
  quotedMsg,
  onClose,
  from,
  handleClick,
  isMobile,
  msgSender,
}: {
  quotedMsg: Message;
  onClose?: () => void;
  from: QuotedFrom;
  handleClick?: (quotedMsg: Message) => void;
  isMobile?: boolean;
  msgSender?: MessageSender;
}) {
  // 1. 完全移除状态和 useEffect，改用 useMemo 计算值
  const content = useMemo(
    () => tryParse(quotedMsg?.content),
    [quotedMsg?.content],
  );

  const { msgContent, quoteRedrawn } = useMemo(() => {
    if (!quotedMsg || !content) return { msgContent: "", quoteRedrawn: false };

    let contentText = "";
    let isRedrawn = false;

    if (content.fileName) {
      if (content.fileType === MessageType.Video) {
        contentText = `[${Locale.chat.video}]: ${content.fileName}`;
      } else if (content.fileType === MessageType.Image) {
        contentText = `[${Locale.chat.image}]: ${content.fileName}`;
      } else {
        contentText = `[${Locale.chat.file}]: ${content.fileName}`;
      }
    } else if (typeof content.text === "string") {
      if (quotedMsg.hasRedrawn) {
        isRedrawn = true;
        contentText = Locale.chat.quotedHaveRedrawMsg;
      } else {
        contentText = `${content.text}`;
      }
    }

    return { msgContent: contentText, quoteRedrawn: isRedrawn };
  }, [quotedMsg, content]);

  // 2. 确保所有依赖项完整
  const quotedPreview = useMemo(() => {
    if (quotedMsg && isForbiddenMsg(quotedMsg)) {
      return (
        <div
          className={styles.quotedPreview}
          dangerouslySetInnerHTML={{ __html: Locale.riskMsg.content }}
        />
      );
    }
    if (from === QuotedFrom.ChatInput || !content) return null;

    if (content.fileType === MessageType.Video) {
      return (
        <video src={content.fileUrl} width={isMobile ? 200 : 400} controls />
      );
    }
    if (content.fileType === MessageType.Image) {
      return (
        <Image height={200} src={content.fileUrl} alt={content.fileName} />
      );
    }
    if (content.fileName) {
      return <span className={styles.fileContainer}>{content.fileName}</span>;
    }
    if (!content.text) return null;

    return (
      <div
        className={styles.quotedPreview}
        dangerouslySetInnerHTML={{ __html: content.text }}
      />
    );
  }, [content, isMobile, from]); // 添加 from 依赖

  // 3. 安全处理 getPopupContainer
  const getPopupContainer = useCallback(() => {
    const container = document.querySelector('[class^="Chat_chatMessages__"]');
    return container ? (container as HTMLElement) : document.body;
  }, []);

  // 4. 使用 useCallback 优化点击处理
  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose?.();
    },
    [onClose],
  );

  const handleContainerClick = useCallback(() => {
    handleClick?.(quotedMsg);
  }, [handleClick, quotedMsg]);

  if (!quotedMsg) return null;

  return (
    <div
      className={`${styles.chatInputQuoted} quoteMsgContainer ${styles[from]} ${styles[isMobile ? "mobile" : "pc"]}`}
    >
      <Popover
        trigger="hover"
        content={quotedPreview}
        getPopupContainer={getPopupContainer}
        position={msgSender === MessageSender.USER ? "left" : "right"}
        // 添加 key 强制重新创建 Popover 当内容变化
        key={`popover-${quotedMsg.id}`}
      >
        <div className={styles.container} onClick={handleContainerClick}>
          {typeof onClose === "function" && (
            <SvgClose className={styles.closeIcon} onClick={handleCloseClick} />
          )}
          <span className={styles.divider}></span>
          {!quoteRedrawn && (
            <span className={styles.reply}>{Locale.chat.reply}: </span>
          )}
          {isForbiddenMsg(quotedMsg) ? (
            <span className={styles.mainContent}>{Locale.riskMsg.content}</span>
          ) : (
            <span className={styles.mainContent}>{msgContent}</span>
          )}
        </div>
      </Popover>
    </div>
  );
}
